import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Play, Clock, BookOpen, ArrowRight, Loader2, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BBI_LOGO = "https://customer-assets.emergentagent.com/job_54aeb419-6630-419e-b73e-06cd0626cb80/artifacts/xw2ii5qd_AD1A694D-1FDF-43F3-8C4C-B0BC17C37F1E.png";

const SchoolPage = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrollments, setEnrollments] = useState([]);
  const [processingCourse, setProcessingCourse] = useState(null);
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed courses first
        await axios.post(`${API_URL}/api/seed/courses`);
        
        const coursesRes = await axios.get(`${API_URL}/api/courses`);
        setCourses(coursesRes.data);

        if (user) {
          try {
            const enrollmentsRes = await axios.get(`${API_URL}/api/enrollments`, {
              headers: getAuthHeader()
            });
            setEnrollments(enrollmentsRes.data);
          } catch (e) {
            console.log("No enrollments found");
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, getAuthHeader]);

  const isEnrolled = (courseId) => {
    return enrollments.some(e => e.course_id === courseId);
  };

  const handleEnroll = async (course) => {
    if (!user) {
      toast.info("Please sign in to enroll in courses");
      navigate("/login", { state: { from: "/school" } });
      return;
    }

    if (isEnrolled(course.id)) {
      navigate(`/school/course/${course.id}`);
      return;
    }

    setProcessingCourse(course.id);
    try {
      const response = await axios.post(
        `${API_URL}/api/payments/checkout`,
        {
          course_id: course.id,
          origin_url: window.location.origin
        },
        { headers: getAuthHeader() }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error("Unable to process enrollment. Please try again.");
    } finally {
      setProcessingCourse(null);
    }
  };

  const getTotalDuration = (modules) => {
    return modules.reduce((acc, m) => acc + (m.duration_minutes || 0), 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }

  return (
    <div data-testid="school-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1E3A5F] via-[#162c47] to-[#0f1e32] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <span className="text-[#9D9045] font-medium text-sm tracking-wider uppercase">Business Bootcamp Institute</span>
              <h1 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Digital Business School
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Self-paced online courses designed for entrepreneurs. Learn the fundamentals of business structure, money management, planning, and credit building from industry experts.
              </p>
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <div className="flex items-center gap-2 text-gray-300">
                  <BookOpen className="h-5 w-5 text-[#9D9045]" />
                  <span>{courses.length} Courses</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <Play className="h-5 w-5 text-[#9D9045]" />
                  <span>Video Modules</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <Clock className="h-5 w-5 text-[#9D9045]" />
                  <span>Lifetime Access</span>
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              <img 
                src={BBI_LOGO} 
                alt="Business Bootcamp Institute" 
                className="w-72 h-72 object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Course Categories */}
      <section className="py-8 bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-wrap gap-4 justify-center">
            {["All Courses", "Business Structure", "Money Management", "Business Planning", "Business Credit"].map((cat, i) => (
              <button
                key={i}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  i === 0 
                    ? "bg-[#1E3A5F] text-white" 
                    : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {courses.map((course) => {
              const enrolled = isEnrolled(course.id);
              const duration = getTotalDuration(course.modules || []);
              
              return (
                <div 
                  key={course.id} 
                  className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col"
                  data-testid={`course-card-${course.id}`}
                >
                  <div className="h-48 relative overflow-hidden">
                    <img 
                      src={course.image_url} 
                      alt={course.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <span className="inline-block px-3 py-1 bg-[#9D9045] text-white text-xs font-medium rounded-full">
                        {course.category}
                      </span>
                    </div>
                    {enrolled && (
                      <div className="absolute top-4 right-4 bg-green-500 text-white text-xs font-medium px-3 py-1 rounded-full">
                        Enrolled
                      </div>
                    )}
                  </div>
                  
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="text-xl font-semibold text-[#1E3A5F] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {course.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 flex-1">
                      {course.description}
                    </p>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                      <div className="flex items-center gap-1">
                        <BookOpen className="h-4 w-4" />
                        <span>{course.modules?.length || 0} modules</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>{duration} min</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="text-2xl font-bold text-[#1E3A5F]">
                        ${course.price?.toFixed(0)}
                        <span className="text-sm font-normal text-gray-500 ml-1">one-time</span>
                      </div>
                      
                      <Button 
                        onClick={() => handleEnroll(course)}
                        disabled={processingCourse === course.id}
                        className={enrolled 
                          ? "bg-green-600 hover:bg-green-700 text-white" 
                          : "bg-[#1E3A5F] hover:bg-[#162c47] text-white"
                        }
                        data-testid={`enroll-course-${course.id}`}
                      >
                        {processingCourse === course.id ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Processing...
                          </>
                        ) : enrolled ? (
                          <>
                            <Play className="mr-2 h-4 w-4" />
                            Continue Learning
                          </>
                        ) : (
                          <>
                            Enroll Now <ArrowRight className="ml-2 h-4 w-4" />
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
              What You'll Get
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Play,
                title: "Video Training",
                desc: "Professional video modules you can watch at your own pace, anytime, anywhere."
              },
              {
                icon: BookOpen,
                title: "Practical Knowledge",
                desc: "Real-world business strategies and techniques you can apply immediately."
              },
              {
                icon: Lock,
                title: "Lifetime Access",
                desc: "Once enrolled, you have unlimited access to course materials forever."
              }
            ].map((item, i) => (
              <div key={i} className="text-center p-8 bg-gray-50 rounded-xl">
                <div className="h-14 w-14 rounded-full bg-[#1E3A5F]/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="h-7 w-7 text-[#1E3A5F]" />
                </div>
                <h4 className="font-semibold text-[#1E3A5F] text-lg mb-2">{item.title}</h4>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1E3A5F]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Start Learning?
          </h2>
          <p className="text-gray-300 mb-8 text-lg">
            Join hundreds of entrepreneurs who have transformed their business knowledge through our courses.
          </p>
          {!user && (
            <Link to="/register">
              <Button className="bg-[#9D9045] hover:bg-[#8a7e3c] text-white px-8 py-6 text-lg" data-testid="school-register-btn">
                Create Free Account
              </Button>
            </Link>
          )}
        </div>
      </section>
    </div>
  );
};

export default SchoolPage;
